/**
 * Resolve edge-flag variant assignments on the client, from the same
 * `lis_bucket` cookie + `pickVariant`/`isEnrolled` primitives the CloudFront
 * viewer-request function uses. Identical bucketing logic, just sourced from
 * `document.cookie` instead of a request header.
 *
 * Why this exists: TanStack `__root.tsx`'s `beforeLoad` re-runs on every
 * client-side navigation (verified in
 * `docs/cloned-repos-as-docs/router/packages/router-core/src/load-matches.ts:517`),
 * but the existing SSR resolver (`ssr-variant-resolver.ts`) is wrapped in
 * `createServerOnlyFn` and reads request headers — neither works on the
 * client. Without a client-side resolver, users who land on a non-experimental
 * route (e.g. `/app/...`) and SPA-navigate to `/doc/X` never get fresh
 * `edgeFlags` plumbed through the route context and `useFlag` falls back to
 * the flag's `default`, biasing them all toward control regardless of their
 * actual edge bucket.
 *
 * Same `EdgeFlags` shape, same exclusion rules (category-page disambiguation
 * via `isExperimentalPathExcluded`), same enrollment + variant hash as the
 * edge function so a given `lis_bucket` produces the same variant in both
 * places. The shared primitives live in `@listening/frontend/analytics/bucketing` and
 * are unit-tested against a reference vector.
 */
import type { EdgeFlags } from '@listening/frontend/analytics/client';
import { ClientFlags } from '@listening/frontend/analytics/flags/client';
import {
  asEdgeFlagDef,
  type FeatureFlagDef,
} from '@listening/frontend/analytics/flags/types';
import {
  isEnrolled,
  parseLisBucketCookie,
  pickVariant,
} from '@listening/frontend/analytics/bucketing';
import { getRouteExperiments } from './experimental-routes';
import { isExperimentalPathExcluded } from './experimental-route-exclusions';

/**
 * Compute `EdgeFlags` for the current pathname using the `lis_bucket` cookie
 * from `document.cookie`. Returns `undefined` for non-experimental paths so
 * callers can short-circuit; returns a populated map (with `enrolled: false`
 * for excluded paths or missing cookie) otherwise so `applyEdgeFlags`'s
 * cleanup paths still run.
 *
 * Safe to call repeatedly per navigation — the bucketing primitives are
 * pure and `document.cookie` reads are cheap.
 */
export function resolveEdgeFlagsClient(
  pathname: string,
): EdgeFlags | undefined {
  const experiments = getRouteExperiments(pathname);
  if (experiments.length === 0) return undefined;

  if (isExperimentalPathExcluded(pathname)) {
    return buildNonEnrolledMap(experiments);
  }

  // Cookie should already be set by the server's `ensureBucketCookie` on the
  // initial page load. If somehow absent (private mode, blocked cookies),
  // treat as non-enrolled rather than minting a client-side bucket — the
  // server is the authority on bucket assignment.
  const bucketId = readLisBucketFromDocument();
  if (bucketId === undefined) {
    return buildNonEnrolledMap(experiments);
  }

  const result: EdgeFlags = {};
  for (const flagName of experiments) {
    const def = lookupEdgeFlag(flagName);
    if (!def) continue;
    const enrolled = isEnrolled(bucketId, def.key, def.bucketing.enrollment);
    result[def.key] = enrolled
      ? {
          enrolled: true,
          variant: pickVariant(bucketId, def.key, def.bucketing.split),
        }
      : { enrolled: false };
  }
  return result;
}

function readLisBucketFromDocument(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  return parseLisBucketCookie(document.cookie);
}

function lookupEdgeFlag(flagName: string) {
  return asEdgeFlagDef(
    (ClientFlags as Record<string, FeatureFlagDef>)[flagName],
  );
}

function buildNonEnrolledMap(experiments: ReadonlyArray<string>): EdgeFlags {
  const result: EdgeFlags = {};
  for (const flagName of experiments) {
    const def = lookupEdgeFlag(flagName);
    if (!def) continue;
    result[def.key] = { enrolled: false };
  }
  return result;
}
